import { CourseEnrollment, CourseType } from '../../../apis/api-client';
import { Box, Button, Chip, CircularProgress, Typography } from '../../ui';
import { DownloadIcon } from '../../icons';
import { courseTypeMeta } from '../contentTypeMeta';
import { formatDate } from '../../../utils/roleHelpers';

export interface LearnerActivityTranscriptProps {
  /** Completed enrollments (courses + learning paths). */
  completedRows: CourseEnrollment[];
  loading: boolean;
  onExport: () => void;
  onOpenCourse: (enrollment: CourseEnrollment) => void;
}

/**
 * LearnerActivityTranscript
 *
 * Transcript tab of the learner "Your activity" panel — a formal table
 * of completions with a CSV export button. Presentational; extracted
 * from LearnerActivitySummary.
 */
export default function LearnerActivityTranscript({
  completedRows,
  loading,
  onExport,
  onOpenCourse,
}: LearnerActivityTranscriptProps) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1.5,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Every course or learning path you've completed.
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={onExport}
          disabled={completedRows.length === 0}
          sx={{ textTransform: 'none' }}
        >
          Export CSV
        </Button>
      </Box>
      {completedRows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
          Your transcript is empty — completed content lands here.
        </Typography>
      ) : (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr 1fr 1fr',
              gap: 1.5,
              px: 1.5,
              py: 1,
              bgcolor: 'grey.50',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            {['Course', 'Type', 'Started', 'Completed'].map((h) => (
              <Typography
                key={h}
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}
              >
                {h}
              </Typography>
            ))}
          </Box>
          {completedRows.map((e, idx) => {
            const meta = courseTypeMeta((e as any).courseType as CourseType | undefined);
            return (
              <Box
                key={e.id ?? `${e.userId}-${e.courseId}`}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr 1fr',
                  gap: 1.5,
                  px: 1.5,
                  py: 1,
                  alignItems: 'center',
                  borderBottom: idx === completedRows.length - 1 ? 0 : '1px solid',
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'action.hover' },
                  cursor: 'pointer',
                }}
                onClick={() => onOpenCourse(e)}
              >
                <Typography variant="body2" fontWeight={500} noWrap>
                  {e.courseTitle || `Course #${e.courseId}`}
                </Typography>
                <Box>
                  <Chip
                    label={meta.label}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      bgcolor: `${meta.color}15`,
                      color: meta.color,
                      '& .MuiChip-label': { px: 0.75 },
                    }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(e.startedAt) || '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(e.completedAt) || '—'}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
